import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { z } from "zod";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import FormInputField from "@/components/lib/from-input-field";
import {
  updateExistingVariant,
  clearVariantMessages,
} from "@/redux/slice/essential-menu-slice/variants-slice";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";

export default function UpdateVariantDialog({
  crust = false,
  isOpen,
  onClose,
  title,
  variantData,
  name_field,
}) {
  const dispatch = useDispatch();
  const { updateLoading } = useSelector(
    (state) => state.essentialMenu.variants
  );
  const { crusts } = useSelector((state) => state.essentialMenu.crusts);

  const [showInPrice, setShowInPrice] = useState(
    variantData?.showInPrice ?? true
  );
  const [showOutPrice, setShowOutPrice] = useState(
    variantData?.showOutPrice ?? true
  );

  const form = useForm({
    resolver: zodResolver(variantSchema),
    defaultValues: {
      crust_id: variantData?.crust_id || "",
      name: variantData?.name || "",
      calorie_amount: variantData?.calorie_amount || "",
      in_price: variantData?.in_price || "",
      out_price: variantData?.out_price || "",
      showInPrice: variantData?.showInPrice ?? true,
      showOutPrice: variantData?.showOutPrice ?? true,
    },
  });

  useEffect(() => {
    const parsedData = {
      crust_id: variantData?.crust_id || "",
      name: variantData?.name || "",
      calorie_amount: variantData?.calorie_amount || "",
      in_price: variantData?.in_price || "0",
      out_price: variantData?.out_price || "0",
      showInPrice: variantData?.in_price ? true : false,
      showOutPrice: variantData?.out_price ? true : false,
    };

    form.reset(parsedData);
    setShowInPrice(parsedData.showInPrice);
    setShowOutPrice(parsedData.showOutPrice);
  }, [JSON.stringify(variantData)]);

  const onSubmit = async (data) => {
    const { showInPrice, showOutPrice, ...rest } = data;
    const payload = {
      ...rest,
    };

    try {
      dispatch(clearVariantMessages());
      const res = await dispatch(
        updateExistingVariant({ id: variantData.id, data: payload })
      );

      if (updateExistingVariant.fulfilled.match(res)) {
        toast.success("Variant updated successfully.");
        onClose();
      } else if (updateExistingVariant.rejected.match(res)) {
        toast.error(res.payload || "Update failed.");
      }
    } catch (error) {
      toast.error("Something went wrong.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            {title || "Update Variant"}
          </DialogTitle>
          <Separator className="mt-4" />

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-6 mt-5"
            >
              {crust && (
                <FormInputField
                  name="crust_id"
                  style="bg-cblack3 flex-1"
                  placeholder={"Select Crust"}
                  form={form}
                  type="select"
                  parseAs="number"
                  selectOption={
                    crusts && crusts.length > 0
                      ? crusts.filter((item) => item?.status === "active").map((item) => ({
                          label: item.name,
                          value: item.id,
                        }))
                      : [
                          {
                            label: "No Crust available",
                            value: "",
                            disabled: true,
                          },
                        ]
                  }
                  label={"Crust"}
                />
              )}
              <FormInputField
                name={"name"}
                style="bg-cblack3"
                placeholder={`${crust ? "0 inch" : "Variant Name"}`}
                form={form}
                label={`${crust ? "Size" : `${name_field || "Variant Name"}`}`}
                type="text"
              />

              <FormInputField
                name="calorie_amount"
                style="bg-cblack3"
                placeholder={"Calorie Amount"}
                form={form}
                label={"Calorie Amount "}
                type="text"
              />

              {/* In-Price Toggle */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showInPrice}
                    onCheckedChange={(val) => {
                      setShowInPrice(val);
                      form.setValue("showInPrice", val);
                    }}
                  />
                  <Label>In-Price</Label>
                </div>
                {showInPrice && (
                  <FormInputField
                    name="in_price"
                    placeholder={"Enter amount"}
                    form={form}
                    type="text"
                    style="bg-cblack3"
                  />
                )}
              </div>

              {/* Out-Price Toggle */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showOutPrice}
                    onCheckedChange={(val) => {
                      setShowOutPrice(val);
                      form.setValue("showOutPrice", val);
                    }}
                  />
                  <Label>Out-Price</Label>
                </div>
                {showOutPrice && (
                  <FormInputField
                    name="out_price"
                    placeholder={"Enter amount"}
                    form={form}
                    type="text"
                    style="bg-cblack3"
                  />
                )}
              </div>

              <Button
                isLoading={updateLoading}
                type="submit"
                className="bg-cgreen w-full text-cblack"
              >
                Update
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const variantSchema = z
  .object({
    name: z.string().min(1, "Variant name is required"),

    calorie_amount: z.preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        required_error: "Calorie amount is required",
        invalid_type_error: "Calorie amount must be a number",
      })
    ),
    crust_id: z
      .preprocess(
        (val) => {
          if (val === "" || val == null) return null;
          return Number(val);
        },
        z
          .number({
            invalid_type_error: "Item no must be a number",
          })
          .nullable()
      )
      .optional(),

    in_price: z.preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        required_error: "In-Price is required",
        invalid_type_error: "In-Price must be a number",
      })
    ),

    out_price: z.preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        required_error: "Out-Price is required",
        invalid_type_error: "Out-Price must be a number",
      })
    ),

    showInPrice: z.boolean(),
    showOutPrice: z.boolean(),
  })

  .refine(
    (data) => !data.showInPrice || (data.showInPrice && data.in_price > 0),
    {
      path: ["in_price"],
      message: "In-Price must be greater than 0",
    }
  )
  .refine(
    (data) => !data.showOutPrice || (data.showOutPrice && data.out_price > 0),
    {
      path: ["out_price"],
      message: "Out-Price must be greater than 0",
    }
  );
